import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../container/ConfirmDialog/ConfirmDialog";
import Edit from "../container/Edit";
import { AppContext } from "../helpers/contexts/AppContext";
import AddAccountType from "./AddAccountType";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { enqueueSnackbar } from "notistack";

export default function AccountTypes() {
  const endpoint = "account-type";
  const accTypesViewUrl = "account-types";
  const { REACT_APP_ACCOUNTS_API_URL } = process.env;
  const navigate = useNavigate();
  const axios = useAxiosPrivate();
  const skipStart = 0;
  const skipEnd = -1;
  const displayStart = 0;
  const displayEnd = -1;
  const nonUpdatableFields = ["id", "type"];

  const [accountTypes, setAccountTypes] = React.useState([]);

  const {
    openConfirmDialog,
    handleCloseConfirmDialog,
    handleOpenConfirmDialog,
    handleCloseEditDialog,
    handleOpenEditDialog,
    openEditDialog,
    edit,
    auth,
  } = React.useContext(AppContext);

  const [confirmMessage, setConfirmMessage] = React.useState("");
  const [dialogHeader, setDialogHeader] = React.useState("");
  const [action, setAction] = React.useState(null);
  const [actionPresent, setactionPresent] = React.useState(false);
  const [payload, setPayload] = React.useState(null);
  const [editDialogHeader, setEditDialogHeader] = React.useState("");
  const [editActionPresent, setEditActionPresent] = React.useState(false);
  const [param, setParam] = React.useState(null);
  const [editRow, setEditRow] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  const deleteAccountType = (id) => {
    axios
      .delete(`${REACT_APP_ACCOUNTS_API_URL}/${endpoint}/${id}`)
      .then((res) => {
        if (res.status && res.status === 200) {
          setAccountTypes(accountTypes.filter((acc) => acc.id !== id));
          return enqueueSnackbar("Account type deleted successfully.", {
            variant: "success",
          });
        }
      })
      .catch((err) => {
        return enqueueSnackbar(`${err.response.data.apiErrors[0].cause}`, {
          variant: "error",
        });
      });
  };

  const handleView = (e, params) => {
    e.stopPropagation(); // don't select this row after clicking

    const api = params.api;
    const thisRow = {};

    api
      .getAllColumns()
      .filter((c) => c.field !== "__check__" && !!c)
      .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));
    return navigate(`/${accTypesViewUrl}/${thisRow.id}`);
  };

  const handleEdit = (e, params) => {
    e.stopPropagation(); // don't select this row after clicking

    const api = params.api;
    const thisRow = {};

    api
      .getAllColumns()
      .filter((c) => c.field !== "__check__" && !!c)
      .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));

    setEditRow(thisRow);
    setEditDialogHeader("Update Roles Details");
    setEditActionPresent(true);
    setAction("edit");
    setParam(thisRow.id);
    handleOpenEditDialog();
  };

  const handleDelete = (e, params) => {
    e.stopPropagation(); // don't select this row after clicking

    const api = params.api;
    const thisRow = {};

    api
      .getAllColumns()
      .filter((c) => c.field !== "__check__" && !!c)
      .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));
    setConfirmMessage(
      `Are you sure you want to delete account type ${thisRow.account_type}?`
    );
    setDialogHeader("Delete Account Type");
    setactionPresent(true);
    setAction("delete");
    setPayload(thisRow.id);
    handleOpenConfirmDialog();
  };

  const actions = [
    {
      name: "view",
      onclick: (e, params) => handleView(e, params),
      icon: <ChromeReaderModeIcon />,
      color: "primary",
      role: "VIEW_ACCOUNT_TYPES",
    },
    {
      name: "edit",
      onclick: (e, params) => handleEdit(e, params),
      icon: <EditIcon />,
      color: "primary",
      role: "EDIT_ACCOUNT_TYPES",
    },
    {
      name: "delete",
      onclick: (e, params) => handleDelete(e, params),
      icon: <DeleteIcon />,
      color: "warning",
      role: "DELETE_ACCOUNT_TYPES",
    },
  ];

  const columns = [
    { field: "id", headerName: "ID", width: 50 },
    { field: "account_type", headerName: "Type", width: 100 },
    {
      field: "allowed_daily_deposits",
      headerName: "Allowed Daily Deposits",
      width: 200,
    },
    {
      field: "allowed_daily_withdrawals",
      headerName: "Alowed Daily Withdrawal",
      width: 200,
    },
    { field: "transaction_limit", headerName: "Transaction Limit", width: 150 },
    {
      field: "allowed_transactions_per_min",
      headerName: "Transaction Per min",
      width: 150,
    },

    {
      field: "action",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => {
        return actions.map((action) =>
          auth?.permissions?.find(
            (perm) => action.role.includes(perm) || perm === "ROOT"
          ) ? (
            <Tooltip title={action.name}>
              <Button
                variant="outlined"
                startIcon={action.icon}
                sx={{ m: 1 }}
                onClick={(e) => action.onclick(e, params)}
                color={action.color}
                aria-label={action.label}
                key={action.name}
              >
                {action.label}
              </Button>
            </Tooltip>
          ) : (
            ""
          )
        );
      },
      width: 250,
    },
  ];

  React.useEffect(() => {
    setLoading(true);
    let types = [];
    axios
      .get(`${REACT_APP_ACCOUNTS_API_URL}/${endpoint}/all`)
      .then((res) => {
        const { result } = res.data;
        result.map((row) => {
          types.push(row);
        });
        setAccountTypes(types);
      })
      .then((_) => setLoading(false));
  }, []);

  return (
    <React.Fragment>
      {openConfirmDialog && (
        <ConfirmDialog
          openConfirmDialog={openConfirmDialog}
          handleOpenConfirmDialog={() => handleOpenConfirmDialog()}
          handleCloseConfirmDialog={handleCloseConfirmDialog}
          dialogHeader={dialogHeader}
          message={confirmMessage}
          actionPresent={actionPresent}
          action={action}
          handler={deleteAccountType}
          payload={payload}
        />
      )}
      {openEditDialog && (
        <Edit
          openEditDialog={openEditDialog}
          handleOpenEditDialog={handleOpenEditDialog}
          handleCloseEditDialog={handleCloseEditDialog}
          dialogHeader={editDialogHeader}
          actionPresent={editActionPresent}
          action={action}
          handler={edit}
          param={param}
          row={editRow}
          skipStart={skipStart}
          skipEnd={skipEnd}
          displayStart={displayStart}
          displayEnd={displayEnd}
          url={REACT_APP_ACCOUNTS_API_URL}
          endpoint={endpoint}
          collection={accountTypes}
          entity="account type"
          setter={setAccountTypes}
          nonUpdatableFields={nonUpdatableFields}
        />
      )}
      <AddAccountType />
      <div style={{ width: "100%" }}>
        <DataGrid
          autoHeight
          rows={accountTypes}
          columns={columns}
          rowsPerPageOptions={[20, 50, 100]}
          getRowHeight={() => "auto"}
          disableColumnMenu
          loading={loading}
        />
      </div>
    </React.Fragment>
  );
}
